import React, { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from './Header';
import Footer from './Footer';
import CartSidebar from '../Cart/CartSidebar';
import WhatsAppButton from '../Common/WhatsAppButton';
import { getCurrentUser, selectIsAuthenticated, selectAuthLoading, selectUser } from '../../store/slices/authSlice';
import { fetchCart } from '../../store/slices/cartSlice';

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authLoading = useSelector(selectAuthLoading);
  const user = useSelector(selectUser);
  const attemptedAuthRef = useRef(false);
  
  // Debug user state
  useEffect(() => {
    console.log('[Layout] Current user state:', JSON.stringify({
      user: user ? {
        id: user.id,
        email: user.email,
        role: user.role,
        first_name: user.first_name,
        last_name: user.last_name
      } : null,
      isAuthenticated,
      role: user?.role
    }, null, 2));
    console.log('[Layout] Is authenticated:', !!user?.id);
    console.log('[Layout] User role:', user?.role);
    console.log('[Layout] Access token exists:', !!localStorage.getItem('access_token'));
    console.log('[Layout] Refresh token exists:', !!localStorage.getItem('refresh_token'));
  }, [user, isAuthenticated]);

  useEffect(() => {
    // Ensure this auth check only runs once per app mount to avoid loops on 401/403
    if (attemptedAuthRef.current) return;

    const token = localStorage.getItem('access_token');
    if (token === 'null' || token === 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
    const normalizedToken = (token === 'null' || token === 'undefined') ? null : token;
    console.log('[Layout] Auth initialization check:', JSON.stringify({ 
      hasAccessToken: !!normalizedToken,
      hasRefreshToken: !!localStorage.getItem('refresh_token'),
      isAuthenticated, 
      authLoading,
      userPresent: !!user
    }, null, 2));

    // Mark as attempted regardless to prevent repeated dispatches on render churn
    attemptedAuthRef.current = true;

    if (normalizedToken && !isAuthenticated && !authLoading) {
      console.log('[Layout] Dispatching getCurrentUser to load user data');
      dispatch(getCurrentUser())
        .unwrap()
        .then(userData => {
          console.log('[Layout] User data loaded successfully:', userData);
        })
        .catch(error => {
          // If we get 401 Unauthorized, the token is invalid - clear it silently
          if (error?.status === 401 || error?.message?.includes('401')) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
          } else {
            // Only log non-401 errors
            console.error('[Layout] Failed to load user data:', error);
          }
        });
    }
  }, [dispatch, isAuthenticated, authLoading, user]);

  useEffect(() => {
    // Fetch cart when user logs in or on app start
    if (isAuthenticated) {
      dispatch(fetchCart());
    }
  }, [dispatch, isAuthenticated]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-1">
        {children ?? <Outlet />}
      </main>
      
      <Footer />
      
      {/* Cart Sidebar */}
      <CartSidebar />

      {/* Floating WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
